/**
 * A delivered live lesson's one status line (redesign spec, batch 6), for
 * the player and for the coach, or none for a normal lesson; and which
 * action goes with it. Pure, so it is unit tested.
 */
import { firstNameOf } from './liveLessonText.ts';
import { playerNoShowStatus } from './playerNoShow.ts';
import type { LiveCallBooking } from '../types/liveCall';

export interface DeliveredBooking {
  status: string;
  coachName?: string;
  playerName?: string;
  membershipId?: string;
  lateCancel?: boolean;
  missDecision?: string;
  decisionDueAt?: string;
  coachJoinedAt?: string;
  playerJoinedAt?: string;
  /** The server's (rules.attendanceOf; booking-contracts.md 8): ranShort is the minutes when it ran short, else
   *  null, 0 included; it was a boolean before UI/UX's 2784722f and its backfill. */
  attendance?: { ranShort: number | boolean | null; togetherMinutes?: number } | null;
  finishedAt?: string;
  /** Decisions 84 and 91: a single the coach missed, held for the player; 154: a player no-show, the coach's. */
  missResolution?: { status: string; reason?: string; dueAt?: string; decidedBy?: string } | null;
}

/** held: a lesson held for the player after the coach's connection dropped, Suggest New Times and Refund instead. */
/** resolve: the coach's Count This Lesson and Offer a New Time (154); suggest: the player's Suggest New Times on one offered. */
export type DeliveredAction = 'rebook' | 'message' | 'report' | 'decide' | 'held' | 'resolve' | 'suggest' | null;

export interface DeliveredStatus {
  line: string | null;
  action: DeliveredAction;
}

/** Whether a delivered list shows this lesson at all: a plain cancel is not a delivered lesson. */
export function showsInDelivered(b: DeliveredBooking): boolean {
  if (b.status === 'cancelled_coach') return false;
  if (b.status === 'cancelled_player') return !!b.lateCancel;
  return true;
}

export function deliveredStatus(b: DeliveredBooking, isCoach: boolean, nowMs: number, tz = 'America/New_York'): DeliveredStatus & { note?: string | null } {
  // A player no-show is the coach's to decide (154, 155): its own lines, read before the held-miss ones.
  const pns = playerNoShowStatus(b, isCoach, tz);
  if (pns) return { line: pns.line, action: pns.action, note: pns.note };
  const coach = firstNameOf(b.coachName, 'Your coach');
  const player = firstNameOf(b.playerName, 'your player');
  const returned = b.missDecision === 'returned';
  const decidable = b.missDecision === 'pending' && !!b.decisionDueAt && nowMs < new Date(b.decisionDueAt).getTime();
  const neither = !b.coachJoinedAt && !b.playerJoinedAt;

  // A single the coach missed is held, not refunded (84): who acts next, then where it went.
  const miss = b.missResolution?.status;
  const Player = firstNameOf(b.playerName, 'Your player');
  // The coach's connection dropped (booking-contracts.md 9): the coach joined, so it is said as such, with the
  // player's two ways on, on the row as on the page.
  if (miss === 'pending' && !isCoach && b.missResolution?.reason === 'coach_interruption') {
    return { line: `${coach}'s connection dropped. Your lesson is held for you.`, action: 'held' };
  }
  if (miss === 'pending') return { line: isCoach ? `${Player} can pick a new time.` : 'Held for you. Pick a new time.', action: null };
  if (miss === 'rebooked') return { line: 'Booked again at no charge.', action: null };
  if (miss === 'refunded') return { line: isCoach ? `${Player} took a refund.` : 'Refunded in full.', action: null };

  if ((b.status === 'no_show_coach' || b.status === 'no_show_player') && neither) {
    return isCoach
      ? { line: `This lesson didn't happen. Message ${player} to rebook.`, action: 'message' }
      : { line: `This lesson didn't happen. Message ${coach} to rebook.`, action: 'message' };
  }
  if (b.status === 'no_show_coach') {
    return isCoach
      ? { line: `You missed this lesson. ${player}'s lesson was returned.`, action: null }
      : { line: `${coach} couldn't make it. This lesson wasn't used.`, action: 'rebook' };
  }
  if (b.status === 'no_show_player') {
    if (isCoach) {
      if (returned) return { line: `You returned this lesson to ${player}.`, action: null };
      if (!b.missDecision) return { line: `${player} missed this lesson. It was the first, so it went back to ${player}.`, action: null };
      return decidable
        ? { line: `${player} missed this lesson. This used one of ${player}'s lessons for the month. If you still want to do this lesson, you can return it; if not, it stays used. Your call.`, action: 'decide' }
        : { line: `${player} missed this lesson. It was used.`, action: null };
    }
    // A single was paid for itself: there is no month for it to go back into (decision 109).
    if (!b.membershipId) return { line: `You missed this lesson and it was charged. If something came up, message ${coach}.`, action: null };
    if (returned) return { line: `${coach} returned this lesson. It wasn't used.`, action: null };
    if (!b.missDecision) return { line: 'You missed this lesson. It was your first, so it went back into your month.', action: null };
    return { line: `You missed this lesson. One lesson credit was used. If something came up, message ${coach}.`, action: null };
  }
  if (b.status === 'cancelled_player' && b.lateCancel) {
    if (isCoach) {
      if (returned) return { line: `You returned this lesson to ${player}.`, action: null };
      return decidable
        ? { line: `${player} cancelled late. This used one of ${player}'s lessons for the month. If you still want to do this lesson, you can return it; if not, it stays used. Your call.`, action: 'decide' }
        : { line: `${player} cancelled late. It was used.`, action: null };
    }
    if (returned) return { line: `${coach} returned this lesson. It wasn't used.`, action: null };
    return { line: `You cancelled late, so this lesson was used unless ${coach} returns it.`, action: null };
  }
  // How long it ran when that was short: the minutes, checked with != null (0 is a short run), never the old flag
  // ("This lesson ran true minutes"); a flag left over reads togetherMinutes. No Report a Problem on a live
  // lesson (decision 107).
  const short = b.attendance?.ranShort;
  const ran = typeof short === 'number' ? short : short === true ? b.attendance?.togetherMinutes : undefined;
  if (b.status === 'completed' && ran != null) return { line: `This lesson ran ${ran} minutes.`, action: null };
  // Finished and still settling: it is in Delivered from the finish on (finding 18), with what comes next.
  // No lesson notes on a live lesson (decision 107): only the recording is coming.
  if (b.status === 'confirmed' && b.finishedAt) return { line: 'Your recording will be ready in a few minutes', action: null };
  return { line: null, action: null };
}

/** "Fri, Oct 23", the last day to decide. */
export function decideByLine(dueAt: string, tz: string): string {
  return `You have until ${new Date(dueAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz })} to decide.`;
}

/** The coach's Follow Up, in the composer to edit and send (Ryan, 29 Oct): "Nice work today, Ana. Want to set up the next one?" */
export function followUpText(b: { playerName?: string }): string {
  return `Nice work today, ${firstNameOf(b.playerName, 'there')}. Want to set up the next one?`;
}

/** The lesson's length: the minutes both were in, else its booked length. */
export function lessonMinutes(b: LiveCallBooking): number {
  return b.attendance?.togetherMinutes || b.durationMinutes || 45;
}


/**
 * The thread's line when a lesson finishes (Ryan, 28 Oct): "Lesson finished ·
 * 45 min", for the server's delivered_ready line. That message names no
 * lesson, so this takes the pair's lesson that finished last at or before
 * it; "Lesson finished" alone when none is found.
 */
export function finishedThreadLine(atIso: string, bookings: LiveCallBooking[]): string {
  const at = Date.parse(atIso) + 60_000;
  const done = bookings
    .filter((b) => b.status === 'completed' || !!b.finishedAt)
    .map((b) => ({ b, t: Date.parse(b.finishedAt ?? b.scheduledStartAt) }))
    .filter((x) => x.t <= at)
    .sort((x, y) => y.t - x.t)[0];
  return done ? `Lesson finished · ${lessonMinutes(done.b)} min` : 'Lesson finished';
}

/**
 * A Delivered row's line 3 beside its chip, at most four words (decision 135; design/live-lesson-information.md
 * 1): "Lesson credit used", "Charged", "Pick a new time", "Refunded in full"; the coach's "Ana picking a time",
 * "Ana was refunded", "Credit used". The full line, and its action, are the lesson page's (deliveredStatus).
 * Null when the chip says it all.
 */
export function deliveredShort(b: DeliveredBooking, isCoach: boolean, nowMs: number): string | null {
  const pns = playerNoShowStatus(b, isCoach, 'UTC');
  if (pns) return pns.short;
  const Player = firstNameOf(b.playerName, 'Your player');
  const returned = b.missDecision === 'returned';
  const neither = !b.coachJoinedAt && !b.playerJoinedAt;
  const miss = b.missResolution?.status;
  if (miss === 'pending') return isCoach ? `${Player} picking a time` : 'Pick a new time';
  if (miss === 'rebooked') return 'Rebooked';
  if (miss === 'refunded') return isCoach ? `${Player} was refunded` : 'Refunded in full';
  if ((b.status === 'no_show_coach' || b.status === 'no_show_player') && neither) return "Didn't happen";
  if (b.status === 'no_show_coach') return isCoach ? 'Lesson returned' : 'Lesson not used';
  if (b.status === 'no_show_player') {
    if (returned) return 'Credit returned';
    if (isCoach) return b.missDecision ? 'Credit used' : 'First, not used';
    if (!b.membershipId) return 'Charged';
    return b.missDecision ? 'Lesson credit used' : 'Credit not used';
  }
  if (b.status === 'cancelled_player' && b.lateCancel) return returned ? 'Credit returned' : 'Cancelled late';
  const short = b.attendance?.ranShort;
  const ran = typeof short === 'number' ? short : short === true ? b.attendance?.togetherMinutes : undefined;
  // Never the duration on a row (135): the minutes are the page's.
  if (b.status === 'completed' && ran != null) return 'Ran short';
  void nowMs;
  return null;
}
